import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EventPopup — Site-wide event popup (e.g. Sunday brunch).
 * - Loads config from GET /api/settings/public
 * - Shows after `popup_delay_seconds` when enabled
 * - Dismissed state persists per browser session (sessionStorage)
 * - CTA navigates to /reservations with pre-filled query params (date/time/notes)
 */
const EventPopup = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let timer = null;
    axios.get(`${API}/settings/public`).then((r) => {
      const events = (r.data && r.data.popup_events) || [];
      // Pick the first enabled event whose date is in the future (or has no date)
      const todayISO = new Date().toISOString().slice(0, 10);
      const pick = events.find((e) => {
        if (!e || !e.enabled) return false;
        if (!e.date) return true;
        return e.date >= todayISO;
      });
      if (!pick) return;
      // Session dismissal is keyed to the event id so a new event can appear
      const dismissKey = `beni_event_popup_dismissed_${pick.id || pick.title_fr || ''}`;
      if (sessionStorage.getItem(dismissKey) === '1') return;
      setEvent({ ...pick, __dismissKey: dismissKey });
      const delay = Math.max(0, Math.min(60, parseInt(pick.delay_seconds ?? 4, 10) || 0)) * 1000;
      timer = setTimeout(() => setVisible(true), delay);
    }).catch(() => {});
    return () => { if (timer) clearTimeout(timer); };
  }, []);

  if (!event || !visible) return null;

  const dismiss = () => {
    sessionStorage.setItem(event.__dismissKey, '1');
    setVisible(false);
  };

  const handleReserve = () => {
    const params = new URLSearchParams();
    if (event.date) params.set('date', event.date);
    if (event.time) params.set('time', event.time);
    if (event.notes) params.set('notes', event.notes);
    params.set('event', event.id || 'popup');
    sessionStorage.setItem(event.__dismissKey, '1');
    setVisible(false);
    navigate(`/reservations?${params.toString()}`);
  };

  const title = event[`title_${language}`] || event.title_fr || '';
  const description = event[`description_${language}`] || event.description_fr || '';
  const cta = event[`cta_text_${language}`] || event.cta_text_fr || 'Réserver';

  const dateLabel = (() => {
    if (!event.date) return '';
    try {
      const d = new Date(event.date + 'T00:00:00');
      return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
        weekday: 'long', day: 'numeric', month: 'long',
      });
    } catch { return event.date; }
  })();

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-4 animate-fade-in"
      onClick={dismiss}
      data-testid="event-popup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0d0d0d] border border-[#d4af37]/30 w-full md:max-w-md md:rounded-lg rounded-t-2xl shadow-2xl overflow-hidden animate-slide-up"
        data-testid="event-popup"
      >
        {event.image_url ? (
          <div className="w-full h-40 md:h-56 bg-black/40 overflow-hidden">
            {(() => {
              const url = event.image_url;
              const isVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
              const src = url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
              if (isVideo) {
                return (
                  <video
                    src={src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                    data-testid="event-popup-video"
                  />
                );
              }
              return <img src={src} alt={title} className="w-full h-full object-cover" data-testid="event-popup-image" />;
            })()}
          </div>
        ) : null}
        <div className="p-5 md:p-6 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37]">Événement</p>
              <h2 className="text-white text-2xl font-display leading-tight" data-testid="event-popup-title">{title}</h2>
            </div>
            <button onClick={dismiss} className="text-white/40 hover:text-white p-1.5 -m-1.5" aria-label="Fermer" data-testid="event-popup-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {dateLabel && (
            <p className="text-[#d4af37] text-sm font-medium capitalize">
              {dateLabel}{event.time ? ` · ${event.time}` : ''}
            </p>
          )}

          {description && (
            <p className="text-white/70 text-sm whitespace-pre-line leading-relaxed" data-testid="event-popup-description">{description}</p>
          )}

          <div className="flex flex-col-reverse md:flex-row gap-2 pt-2">
            <button
              onClick={dismiss}
              className="flex-1 h-11 text-white/70 border border-white/15 rounded hover:bg-white/5 text-sm transition"
              data-testid="event-popup-dismiss"
            >
              {language === 'fr' ? 'Fermer' : language === 'pt' ? 'Fechar' : 'Close'}
            </button>
            <button
              onClick={handleReserve}
              className="flex-1 h-11 bg-[#d4af37] text-black font-medium rounded hover:bg-white transition text-sm active:scale-[0.99]"
              data-testid="event-popup-cta"
            >
              {cta}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
        .animate-slide-up { animation: slide-up 0.35s cubic-bezier(0.22, 1, 0.36, 1); }
      `}</style>
    </div>
  );
};

export default EventPopup;
